import { Heading } from '@mm/components';
import { CheckCircleIcon } from '@mm/icons';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import ConnectionsController from '@mm/state/controllers/ConnectionsController';
import { Contact } from '@mm/state/interfaces/contacts';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ComponentDataItem,
  DefaultCell,
  renderDataCell,
  renderFullNameCell,
  renderPlatformCell,
} from '../../../editor/features/contacts/components/ContactsTable/ContactTable';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import { Grid, GridCell, GridRow } from '../../components/Grid/Grid';
import { PaginationNav } from '../../components/PaginationNav/PaginationNav';
import { ModalInstanceProps } from '../interfaces';
import { ModalBody, ModalHeader } from '../modal-helpers';
import { getWasSuccess } from '../TestConnectionModal/TestConnectionModal';
import './MailchimpTestConnectionModal.pcss';

type Props = ModalInstanceProps & {
  connection: ConnectionModel;
  onClickActivate: () => void;
};

const renderActionCell =
  (onTestClick: (id: string) => void) =>
  ({ item }: { item: Contact }) => {
    const onClickTest = () => {
      onTestClick(item.remoteId);
    };
    return (
      <GridCell overflowable className="action-cell">
        <PrimaryButton color={ButtonColor.secondaryBlue} onClick={onClickTest}>
          Send Test
        </PrimaryButton>
      </GridCell>
    );
  };

const getCells = (onTestClick: (id: string) => void) => {
  const cells: ComponentDataItem[] = [
    {
      label: 'Name',
      keyName: 'full_name',
      Cell: renderFullNameCell,
    },
    {
      label: 'Platform',
      keyName: 'platform',
      Cell: renderPlatformCell,
    },
    {
      label: 'Phone for SMS',
      keyName: 'phone',
      Cell: renderDataCell('phone'),
    },
    {
      label: 'Email',
      keyName: 'email',
      Cell: renderDataCell('email'),
    },
    {
      label: '',
      Cell: renderActionCell(onTestClick),
    },
  ];
  return cells;
};

export const MailchimpTestConnectionModal = (props: Props) => {
  const { closeModal, connection, onClickActivate } = props;
  const { retrieveAll } = useContactsCRUDActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { contacts, pagination } = useSelector(selectContacts);
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);

  useEffect(() => {
    retrieveAll(activeFbPageId, 1, 25, 'with_email');
  }, []);

  const onChangePagination = async page => {
    await retrieveAll(activeFbPageId, page, pagination.perPage, 'with_email');
  };

  const onTestClick = async (remoteUserId: string) => {
    const response = await ConnectionsController.testWebhook(
      activeFbPageId,
      connection.id,
      [],
      [],
      remoteUserId,
    );
    const wasSuccess = getWasSuccess(response);

    if (wasSuccess) {
      setIsConfirmationModal(true);
    }
  };

  const onActivate = async () => {
    onClickActivate();
    setIsConfirmationModal(false);
    closeModal();
  };

  const cells = getCells(onTestClick);

  const gridTemplateColumns = `5% 30% 5% 20% 20% 20%`;

  const className = classnames('mailchimp-test-connection-modal', {
    'mailchimp-test-connection-modal--confirmation': isConfirmationModal,
  });

  return (
    <div className={className}>
      {isConfirmationModal ? (
        <div>
          <div className="mailchimp-test-connection-modal__confirmation-container">
            <CheckCircleIcon
              size={20}
              className="mailchimp-test-connection-modal__confirmation-icon"
            />{' '}
            Test successfully sent to Mailchimp.
          </div>
          <div className="mailchimp-test-connection-modal__buttons-container">
            <PrimaryButton
              color={ButtonColor.secondaryBlue}
              className="mailchimp-test-connection-modal__close-button"
              onClick={() => {
                setIsConfirmationModal(false);
                closeModal();
              }}
            >
              Close
            </PrimaryButton>
            {!connection.active && (
              <PrimaryButton color={ButtonColor.blue} onClick={onActivate}>
                Activate this Integration
              </PrimaryButton>
            )}
          </div>
        </div>
      ) : (
        <>
          <ModalHeader
            className="mailchimp-test-connection-modal__header"
            closeOverride={() => {
              setIsConfirmationModal(false);
              closeModal();
            }}
          >
            <Heading
              className="mailchimp-test-connection-modal__heading"
              size={2}
              text="Choose a sample contact to send connection for"
            />
            {pagination && (
              <PaginationNav
                className="contacts-grid__pagination"
                pagination={pagination}
                onChangePage={onChangePagination}
              />
            )}
          </ModalHeader>
          <ModalBody>
            {contacts.length ? (
              <Grid>
                <div className="mailchimp-test-connection-modal__rows-container">
                  {contacts.map((item: Contact) => (
                    <GridRow
                      key={item.id}
                      gridTemplateColumns={gridTemplateColumns}
                    >
                      {cells.map(({ Cell, keyName }) => {
                        return Cell ? (
                          <Cell item={item} />
                        ) : (
                          <DefaultCell
                            keyName={keyName as string}
                            item={item}
                          />
                        );
                      })}
                    </GridRow>
                  ))}
                </div>
              </Grid>
            ) : (
              <div className="contacts-grid__pages-text">
                There are no contacts containing email address
              </div>
            )}
          </ModalBody>
        </>
      )}
    </div>
  );
};
